import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogoFull, BRAND_COLORS, OUR_SQUARE_COLORS } from './brand';
import {
  LayoutDashboard, MessageSquare, CalendarDays, BookOpen, Target,
  FileText, Users, BarChart3, Shield, Settings, LogOut, Heart,
  ClipboardList, Flag, UserCog, Building2, AlertTriangle, Menu, X,
  User, Mic, GraduationCap, MapPin, Bell, Briefcase, Gift, Building, Eye, Trophy, Route, Sparkles, Home, Hand, Mail, Link, Link2, Search, DollarSign, TrendingUp, Calculator, Wallet, Award, Scale, Gavel, Flame, Globe, ClipboardCheck
} from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import { ROLE_LABELS } from '../lib/utils';

const navByRole = {
  client: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/checkin', icon: Heart, label: 'Daily Check-in' },
    { to: '/my-support', icon: Shield, label: 'My Support Status' },
    { to: '/core8', icon: ClipboardList, label: 'CORE-8 Profile' },
    { to: '/goals', icon: Target, label: 'Goals' },
    { to: '/journey', icon: Sparkles, label: 'My Journey' },
    { to: '/journey-report', icon: FileText, label: 'Journey Report' },
    { to: '/roadmap', icon: Route, label: 'My Roadmap' },
    { to: '/courses', icon: GraduationCap, label: 'Skill Builder' },
    { to: '/bio', icon: User, label: 'My Story' },
    { to: '/my-needs', icon: Search, label: 'My Skills & Needs' },
    { to: '/achievements', icon: Trophy, label: 'Achievements' },
    { to: '/wallet', icon: Wallet, label: 'Resource Wallet' },
    { to: '/alumni', icon: Award, label: 'Alumni Network' },
    { to: '/employment', icon: Briefcase, label: 'Employment Hub' },
    { to: '/appointments', icon: CalendarDays, label: 'Appointments' },
    { to: '/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/opportunities', icon: Gift, label: 'Opportunities' },
    { to: '/scoreboard', icon: Trophy, label: 'Scoreboard' },
    { to: '/resources', icon: BookOpen, label: 'Resources' },
    { to: '/documents', icon: FileText, label: 'Documents' },
  ],
  peer_specialist: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/clients', icon: Users, label: 'My Caseload' },
    { to: '/core8', icon: ClipboardList, label: 'CORE-8 Assessment' },
    { to: '/risk-engine', icon: Shield, label: 'Risk Engine' },
    { to: '/bio', icon: User, label: 'My Profile' },
    { to: '/peer-docs', icon: Mic, label: 'Peer Documentation' },
    { to: '/group-notes', icon: ClipboardList, label: 'Group Notes' },
    { to: '/service-ledger', icon: FileText, label: 'Service Ledger' },
    { to: '/connections', icon: Link, label: 'Connection Requests' },
    { to: '/court', icon: Building, label: 'Court Cases' },
    { to: '/risk', icon: AlertTriangle, label: 'Risk Dashboard' },
    { to: '/housing', icon: Home, label: 'Housing / Transitional Living' },
    { to: '/calendar', icon: CalendarDays, label: 'Calendar' },
    { to: '/appointments', icon: CalendarDays, label: 'Appointments' },
    { to: '/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/opportunities', icon: Gift, label: 'Opportunities' },
    { to: '/referrals', icon: Users, label: 'Referrals' },
    { to: '/group-sessions', icon: Building2, label: 'Group Sessions' },
    { to: '/resources', icon: BookOpen, label: 'Resources' },
    { to: '/geofencing', icon: MapPin, label: 'Location' },
    { to: '/flags', icon: Flag, label: 'Flags' },
    { to: '/scoreboard', icon: Trophy, label: 'Scoreboard' },
    { to: '/digest', icon: Mail, label: 'Weekly Digest' },
  ],
  facilitator: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/heat', icon: Flame, label: 'H.E.A.T. Hub' },
    { to: '/heat/modules', icon: Trophy, label: 'HEAT Modules' },
    { to: '/heat/attendance', icon: CalendarDays, label: 'HEAT Attendance' },
    { to: '/her', icon: Heart, label: 'H.E.R. Hub' },
    { to: '/her/modules', icon: Trophy, label: 'HER Modules' },
    { to: '/her/attendance', icon: CalendarDays, label: 'HER Attendance' },
    { to: '/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/resources', icon: BookOpen, label: 'Resources' },
  ],
  coordinator: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/approval-queue', icon: ClipboardList, label: 'Approval Queue' },
    { to: '/magic-links', icon: Link2, label: 'Onboarding Links' },
    { to: '/core8', icon: ClipboardList, label: 'CORE-8 Assessment' },
    { to: '/risk-engine', icon: Shield, label: 'Risk Engine' },
    { to: '/incidents', icon: AlertTriangle, label: 'Incidents & Compliance' },
    { to: '/clients', icon: Users, label: 'Manage Clients' },
    { to: '/specialists', icon: UserCog, label: 'Specialists' },
    { to: '/programs', icon: GraduationCap, label: 'Programs' },
    { to: '/referral-management', icon: Hand, label: 'Referral Waitlist' },
    { to: '/service-ledger', icon: FileText, label: 'Service Ledger' },
    { to: '/impact', icon: BarChart3, label: 'Impact Summary' },
    { to: '/connections', icon: Link, label: 'Connection Requests' },
    { to: '/court', icon: Building, label: 'Court Cases' },
    { to: '/risk', icon: AlertTriangle, label: 'Risk Dashboard' },
    { to: '/notifications-scheduler', icon: Bell, label: 'Notification Scheduler' },
    { to: '/security', icon: Shield, label: 'Security Dashboard' },
    { to: '/reports/impact', icon: FileText, label: 'Impact Reports' },
    { to: '/reports/roi', icon: Calculator, label: 'ROI Calculator' },
    { to: '/reports/recidivism', icon: TrendingUp, label: 'Recidivism Tracking' },
    { to: '/reports/compliance', icon: ClipboardList, label: 'Compliance Reports' },
    { to: '/scoreboard', icon: Trophy, label: 'Scoreboard' },
    { to: '/housing', icon: Home, label: 'Housing / Transitional Living' },
    { to: '/calendar', icon: CalendarDays, label: 'Calendar' },
    { to: '/peer-docs', icon: Mic, label: 'Peer Documentation' },
    { to: '/group-notes', icon: ClipboardList, label: 'Group Notes' },
    { to: '/opportunities', icon: Gift, label: 'Opportunities' },
    { to: '/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/digest', icon: Mail, label: 'Weekly Digest' },
    { to: '/group-sessions', icon: Building2, label: 'Group Sessions' },
    { to: '/flags', icon: Flag, label: 'Flags' },
    { to: '/resources', icon: BookOpen, label: 'Resources' },
  ],
  court_officer: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/agency-view', icon: Eye, label: 'Agency Oversight' },
    { to: '/court', icon: Building, label: 'Court Cases' },
    { to: '/risk', icon: AlertTriangle, label: 'Risk Dashboard' },
    { to: '/programs', icon: GraduationCap, label: 'Services Offered' },
    { to: '/compliance', icon: Shield, label: 'Compliance' },
    { to: '/clients', icon: Users, label: 'Participant Records' },
    { to: '/group-sessions', icon: Building2, label: 'Group Sessions' },
    { to: '/analytics', icon: BarChart3, label: 'Reports' },
    { to: '/digest', icon: Mail, label: 'Weekly Digest' },
  ],
  referral_source: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/agency-view', icon: Eye, label: 'Agency Oversight' },
    { to: '/court', icon: Building, label: 'Court Cases' },
    { to: '/risk', icon: AlertTriangle, label: 'Risk Dashboard' },
    { to: '/programs', icon: GraduationCap, label: 'Services Offered' },
    { to: '/reports/compliance', icon: FileText, label: 'Compliance Reports' },
    { to: '/reports/roi', icon: Calculator, label: 'ROI Calculator' },
    { to: '/clients', icon: Users, label: 'Participant Records' },
    { to: '/group-sessions', icon: Building2, label: 'Group Sessions' },
    { to: '/analytics', icon: BarChart3, label: 'Reports' },
    { to: '/digest', icon: Mail, label: 'Weekly Digest' },
  ],
  admin: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/approval-queue', icon: ClipboardList, label: 'Approval Queue' },
    { to: '/magic-links', icon: Link2, label: 'Onboarding Links' },
    { to: '/core8', icon: ClipboardList, label: 'CORE-8 Assessment' },
    { to: '/risk-engine', icon: Shield, label: 'Risk Engine' },
    { to: '/incidents', icon: AlertTriangle, label: 'Incidents & Compliance' },
    { to: '/regions', icon: Globe, label: 'Network / Regions' },
    { to: '/users', icon: Users, label: 'User Management' },
    { to: '/programs', icon: GraduationCap, label: 'Programs' },
    { to: '/referral-management', icon: Hand, label: 'Referral Waitlist' },
    { to: '/service-ledger', icon: FileText, label: 'Service Ledger' },
    { to: '/impact', icon: BarChart3, label: 'Impact Summary' },
    { to: '/connections', icon: Link, label: 'Connection Requests' },
    { to: '/security', icon: Shield, label: 'Security Dashboard' },
    { to: '/reports/impact', icon: FileText, label: 'Impact Reports' },
    { to: '/reports/roi', icon: Calculator, label: 'ROI Calculator' },
    { to: '/reports/recidivism', icon: TrendingUp, label: 'Recidivism Tracking' },
    { to: '/reports/compliance', icon: ClipboardList, label: 'Compliance Reports' },
    { to: '/scoreboard', icon: Trophy, label: 'Scoreboard' },
    { to: '/housing', icon: Home, label: 'Housing / Transitional Living' },
    { to: '/calendar', icon: CalendarDays, label: 'Calendar' },
    { to: '/messages', icon: MessageSquare, label: 'All Messages' },
    { to: '/opportunities', icon: Gift, label: 'Opportunities' },
    { to: '/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/digest', icon: Mail, label: 'Weekly Digest' },
    { to: '/peer-docs', icon: Mic, label: 'Peer Documentation' },
    { to: '/group-notes', icon: ClipboardList, label: 'Group Notes' },
    { to: '/compliance', icon: Shield, label: 'Compliance' },
    { to: '/flags', icon: AlertTriangle, label: 'Flags' },
    { to: '/resources', icon: BookOpen, label: 'Resources' },
    { to: '/group-sessions', icon: Building2, label: 'Group Sessions' },
  ],
  resource_partner: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Home' },
    { to: '/partner-portal', icon: Users, label: 'Partner Portal' },
    { to: '/organization-profile', icon: Building, label: 'Organization Profile' },
    { to: '/post-opportunity', icon: Gift, label: 'Post Opportunity' },
    { to: '/my-opportunities', icon: Briefcase, label: 'My Opportunities' },
    { to: '/resources', icon: BookOpen, label: 'Resource Listings' },
    { to: '/referrals', icon: Users, label: 'Referrals' },
    { to: '/insights', icon: Eye, label: 'Insights' },
    { to: '/messages', icon: MessageSquare, label: 'Messages' },
  ],
  housing_coordinator: [
    { to: '/our-square/staff-dashboard', icon: LayoutDashboard, label: 'Staff Dashboard' },
    { to: '/incidents', icon: AlertTriangle, label: 'Incidents & Compliance' },
    { to: '/our-square/intake', icon: FileText, label: 'Intake Applications' },
    { to: '/our-square/onboarding', icon: ClipboardList, label: 'Resident Onboarding' },
    { to: '/our-square/properties', icon: Home, label: 'Properties' },
    { to: '/our-square/residents', icon: Users, label: 'Residents' },
    { to: '/our-square/milestones', icon: Trophy, label: 'Program Milestones' },
    { to: '/our-square/risk-scoring', icon: Shield, label: 'Risk Scoring' },
    { to: '/our-square/documents', icon: FileText, label: 'Documents' },
    { to: '/our-square/referral-sources', icon: Building, label: 'Referral Sources' },
    { to: '/our-square/curriculum', icon: BookOpen, label: 'Curriculum' },
    { to: '/our-square/meetings', icon: ClipboardList, label: 'Meetings' },
    { to: '/our-square/incidents', icon: AlertTriangle, label: 'Incidents' },
    { to: '/our-square/waitlist', icon: FileText, label: 'Waitlist' },
    { to: '/our-square/rent', icon: Briefcase, label: 'Rent Tracker' },
    { to: '/our-square/compliance-dashboard', icon: Shield, label: '10-Point Compliance' },
    { to: '/our-square/compliance-reports', icon: BarChart3, label: 'Compliance Reports' },
    { to: '/our-square/referrals', icon: Hand, label: 'Referrals' },
    { to: '/messages', icon: MessageSquare, label: 'Messages' },
  ],
  housing_staff: [
    { to: '/our-square/staff-dashboard', icon: LayoutDashboard, label: 'Staff Dashboard' },
    { to: '/our-square/intake', icon: FileText, label: 'Intake Applications' },
    { to: '/our-square/onboarding', icon: ClipboardList, label: 'Resident Onboarding' },
    { to: '/our-square/properties', icon: Home, label: 'Properties' },
    { to: '/our-square/residents', icon: Users, label: 'Residents' },
    { to: '/our-square/milestones', icon: Trophy, label: 'Program Milestones' },
    { to: '/our-square/risk-scoring', icon: Shield, label: 'Risk Scoring' },
    { to: '/our-square/documents', icon: FileText, label: 'Documents' },
    { to: '/our-square/meetings', icon: ClipboardList, label: 'Meetings' },
    { to: '/our-square/incidents', icon: AlertTriangle, label: 'Incidents' },
    { to: '/our-square/waitlist', icon: FileText, label: 'Waitlist' },
    { to: '/our-square/compliance-dashboard', icon: Shield, label: '10-Point Compliance' },
    { to: '/our-square/compliance-reports', icon: BarChart3, label: 'Compliance Reports' },
    { to: '/messages', icon: MessageSquare, label: 'Messages' },
  ],
  housing_resident: [
    { to: '/my-progress', icon: LayoutDashboard, label: 'My Progress' },
    { to: '/my-milestones', icon: Trophy, label: 'My Milestones' },
    { to: '/my-documents', icon: FileText, label: 'My Documents' },
    { to: '/my-needs', icon: Search, label: 'My Skills & Needs' },
    { to: '/achievements', icon: Trophy, label: 'Achievements' },
    { to: '/wallet', icon: Wallet, label: 'Resource Wallet' },
    { to: '/alumni', icon: Award, label: 'Alumni Network' },
    { to: '/goals', icon: Target, label: 'My Goals' },
    { to: '/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/resources', icon: BookOpen, label: 'Resources' },
  ],
};

const heatNavByRole = {
  client: [
    { to: '/heat', icon: Flame, label: 'HEAT Hub' },
    { to: '/heat/modules', icon: Trophy, label: 'HEAT Modules' },
    { to: '/heat/signin', icon: CalendarDays, label: 'HEAT Sign-In' },
    { to: '/heat/messages', icon: MessageSquare, label: 'HEAT Messages' },
  ],
  facilitator: [
    { to: '/heat', icon: Flame, label: 'HEAT Hub' },
    { to: '/heat/live-class', icon: Users, label: 'Live Class' },
    { to: '/heat/modules', icon: Trophy, label: 'HEAT Modules' },
    { to: '/heat/session-docs', icon: ClipboardCheck, label: 'Session Docs' },
    { to: '/heat/attendance', icon: CalendarDays, label: 'HEAT Attendance' },
    { to: '/heat/messages', icon: MessageSquare, label: 'HEAT Messages' },
  ],
  coordinator: [
    { to: '/heat', icon: Flame, label: 'HEAT Hub' },
    { to: '/heat/live-class', icon: Users, label: 'Live Class' },
    { to: '/heat/modules', icon: Trophy, label: 'HEAT Modules' },
    { to: '/heat/session-docs', icon: ClipboardCheck, label: 'Session Docs' },
    { to: '/heat/attendance', icon: CalendarDays, label: 'HEAT Attendance' },
    { to: '/heat/facilitators', icon: Users, label: 'HEAT Facilitators' },
    { to: '/heat/messages', icon: MessageSquare, label: 'HEAT Messages' },
  ],
  admin: [
    { to: '/heat', icon: Flame, label: 'HEAT Hub' },
    { to: '/heat/modules', icon: Trophy, label: 'HEAT Modules' },
    { to: '/heat/session-docs', icon: ClipboardCheck, label: 'Session Docs' },
    { to: '/heat/attendance', icon: CalendarDays, label: 'HEAT Attendance' },
    { to: '/heat/facilitators', icon: Users, label: 'HEAT Facilitators' },
    { to: '/heat/messages', icon: MessageSquare, label: 'HEAT Messages' },
  ],
  referral_source: [
    { to: '/heat', icon: Flame, label: 'HEAT Hub' },
    { to: '/heat/referral-portal', icon: FileText, label: 'HEAT Portal' },
    { to: '/heat/facilitators', icon: Users, label: 'HEAT Facilitators' },
    { to: '/heat/messages', icon: MessageSquare, label: 'HEAT Messages' },
  ],
};

const herNavByRole = {
  client: [
    { to: '/her', icon: Heart, label: 'HER Hub' },
    { to: '/her/modules', icon: Trophy, label: 'HER Modules' },
    { to: '/her/signin', icon: CalendarDays, label: 'HER Sign-In' },
    { to: '/her/messages', icon: MessageSquare, label: 'HER Messages' },
  ],
  facilitator: [
    { to: '/her', icon: Heart, label: 'HER Hub' },
    { to: '/her/live-class', icon: Users, label: 'Live Class' },
    { to: '/her/modules', icon: Trophy, label: 'HER Modules' },
    { to: '/her/session-docs', icon: ClipboardCheck, label: 'Session Docs' },
    { to: '/her/attendance', icon: CalendarDays, label: 'HER Attendance' },
    { to: '/her/messages', icon: MessageSquare, label: 'HER Messages' },
  ],
  coordinator: [
    { to: '/her', icon: Heart, label: 'HER Hub' },
    { to: '/her/live-class', icon: Users, label: 'Live Class' },
    { to: '/her/modules', icon: Trophy, label: 'HER Modules' },
    { to: '/her/session-docs', icon: ClipboardCheck, label: 'Session Docs' },
    { to: '/her/attendance', icon: CalendarDays, label: 'HER Attendance' },
    { to: '/her/facilitators', icon: Users, label: 'HER Facilitators' },
    { to: '/her/messages', icon: MessageSquare, label: 'HER Messages' },
  ],
  admin: [
    { to: '/her', icon: Heart, label: 'HER Hub' },
    { to: '/her/modules', icon: Trophy, label: 'HER Modules' },
    { to: '/her/session-docs', icon: ClipboardCheck, label: 'Session Docs' },
    { to: '/her/attendance', icon: CalendarDays, label: 'HER Attendance' },
    { to: '/her/facilitators', icon: Users, label: 'HER Facilitators' },
    { to: '/her/messages', icon: MessageSquare, label: 'HER Messages' },
  ],
  referral_source: [
    { to: '/her', icon: Heart, label: 'HER Hub' },
    { to: '/her/referral-portal', icon: FileText, label: 'HER Portal' },
    { to: '/her/facilitators', icon: Users, label: 'HER Facilitators' },
    { to: '/her/messages', icon: MessageSquare, label: 'HER Messages' },
  ],
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  if (!user) return null;

  // Check if user is a housing resident (either by role or flag)
  const isHousingResident = user.role === 'housing_resident' || (user.role === 'client' && user.is_housing_resident);
  const baseNav = isHousingResident ? navByRole.housing_resident : (navByRole[user.role] || navByRole.client);
  const isHeatRoute = location.pathname.startsWith('/heat');
  const isHerRoute = location.pathname.startsWith('/her');
  const navItems = isHeatRoute
    ? (heatNavByRole[user.role] || heatNavByRole.client)
    : isHerRoute
      ? (herNavByRole[user.role] || herNavByRole.client)
      : baseNav;
  const isOurSquare = ['housing_coordinator', 'housing_staff', 'housing_resident'].includes(user.role) || isHousingResident;
  const brandColors = isOurSquare ? OUR_SQUARE_COLORS : BRAND_COLORS;

  const SidebarContent = () => (
    <div className="flex flex-col h-full" data-testid="sidebar">
      {/* Logo with proper spacing - auto-detects brand based on user role */}
      <div className="px-4 pt-4 pb-3">
        <LogoFull size="sm" className="w-full" />
      </div>

      {/* User info card with brand colors */}
      <div className="px-4 mb-4 mt-2">
        <div 
          className="rounded-xl border px-3 py-2.5"
          style={{ 
            backgroundColor: isOurSquare ? `${OUR_SQUARE_COLORS.accent}15` : `${BRAND_COLORS.lightBg}`,
            borderColor: isOurSquare ? `${OUR_SQUARE_COLORS.accent}30` : `${BRAND_COLORS.primary}20`
          }}
        >
          <p 
            className="text-sm font-semibold truncate"
            style={{ color: isOurSquare ? OUR_SQUARE_COLORS.primary : BRAND_COLORS.primary, fontFamily: "'Open Sans', sans-serif" }}
          >
            {user.full_name}
          </p>
          <p 
            className="text-xs"
            style={{ color: isOurSquare ? OUR_SQUARE_COLORS.secondary : BRAND_COLORS.neutral, fontFamily: "'Open Sans', sans-serif" }}
          >
            {isHousingResident ? 'Housing Resident' : ROLE_LABELS[user.role]}
          </p>
        </div>
      </div>

      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {navItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                isActive
                  ? 'text-white font-medium shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`
            }
            style={({ isActive }) => ({
              backgroundColor: isActive 
                ? (isOurSquare ? OUR_SQUARE_COLORS.primary : BRAND_COLORS.primary)
                : 'transparent',
              fontFamily: "'Open Sans', sans-serif",
            })}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-4.5 h-4.5 shrink-0" size={18} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-3 pb-4 space-y-0.5 border-t border-border pt-3 mt-2">
        <NavLink
          to="/settings"
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
              isActive ? 'bg-teal-600 text-white font-medium' : 'text-slate-600 hover:bg-teal-50'
            }`
          }
          data-testid="nav-settings"
        >
          <Settings size={18} /> Settings
        </NavLink>
        <button
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-500 hover:bg-red-50 hover:text-red-600 w-full transition-all"
          data-testid="logout-button"
        >
          <LogOut size={18} /> Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-3 left-3 z-50 lg:hidden bg-white shadow-md rounded-full"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-toggle"
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </Button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex lg:flex-col w-64 bg-white border-r border-border h-screen sticky top-0 shrink-0">
        <SidebarContent />
      </aside>

      {/* Sidebar - mobile */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-border transform transition-transform duration-300 lg:hidden ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <SidebarContent />
      </aside>
    </>
  );
}
